import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { computed, effect, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { TransactionStore } from '@app/transaction/store/transaction.store';
import { WaterFinalInvoiceService } from '@app/services/water-final-invoice/classes/water-final-invoice-service';
import { WaterNewContractService } from '@app/services/water-new-contract/classes/water-new-contract-service';
import { WaterTransferDocumentService } from '@app/services/water-transfer-document/classes/water-transfer-document-service';
import { WaterCompositeService } from '@app/services/water-composite/services/water-composite.service';
import { WaterTransferDocumentServiceStore } from '@app/services/water-transfer-document/store/water-transfer-document-service.store';
import { WaterNewContractServiceStore } from '@app/services/water-new-contract/store/water-new-contract-service.store';
import { WaterFinalInvoiceServiceStore } from '@app/services/water-final-invoice/store/water-final-invoice-service.store';
import { WaterTransferDocumentServiceService } from '@app/services/water-transfer-document/services/water-transfer-document-service.service';

type WaterCompositeState = {
    isLoading: boolean;
    waterFinalInvoiceService?: WaterFinalInvoiceService;
    waterNewContractService?: WaterNewContractService;
    waterTransferDocumentService?: WaterTransferDocumentService;
};

const defaultState: WaterCompositeState = {
    isLoading: false,
    waterFinalInvoiceService: null,
    waterNewContractService: null,
    waterTransferDocumentService: null,
};

export const WaterCompositeStore = signalStore(
    { providedIn: 'root' },
    withState(defaultState),
    withComputed((state) => {
        return {
            composite: computed(() => {
                return {
                    waterFinalInvoiceService: state?.waterFinalInvoiceService(),
                    waterNewContractService: state?.waterNewContractService(),
                    waterTransferDocumentService: state?.waterTransferDocumentService(),
                };
            }),
        };
    }),
    withMethods(
        (
            store,
            waterCompositeService = inject(WaterCompositeService),
            waterTransferDocumentServiceService = inject(WaterTransferDocumentServiceService),
            waterTransferDocumentServiceStore = inject(WaterTransferDocumentServiceStore),
            waterNewContractServiceStore = inject(WaterNewContractServiceStore),
            waterFinalInvoiceServiceStore = inject(WaterFinalInvoiceServiceStore),
        ) => {
            return {
                reset() {
                    patchState(store, defaultState);
                },

                async load(transactionId: string) {
                    patchState(store, { isLoading: true });

                    waterTransferDocumentServiceStore.set({ isLoading: true });
                    waterNewContractServiceStore.set({ isLoading: true });
                    waterFinalInvoiceServiceStore.set({ isLoading: true });

                    try {
                        const data = await firstValueFrom(waterCompositeService.get(transactionId));

                        const waterFinalInvoiceService = new WaterFinalInvoiceService(data.waterFinalInvoiceService);
                        const waterNewContractService = new WaterNewContractService(data.waterNewContractService);
                        const waterTransferDocumentService = new WaterTransferDocumentService(data.waterTransferDocumentService);

                        patchState(store, {
                            isLoading: false,
                            waterFinalInvoiceService,
                            waterNewContractService,
                            waterTransferDocumentService,
                        });

                        waterTransferDocumentServiceStore.set({ isLoading: false, waterTransferDocumentService });
                        waterNewContractServiceStore.set({ isLoading: false, service: waterNewContractService });
                        waterFinalInvoiceServiceStore.set({ isLoading: false, service: waterFinalInvoiceService });
                    } catch (error) {
                        patchState(store, { isLoading: false });
                        waterTransferDocumentServiceStore.set({ isLoading: false });
                        waterNewContractServiceStore.set({ isLoading: false });
                        waterFinalInvoiceServiceStore.set({ isLoading: false });
                    }
                },

                async updateActive(transactionId: string, active: boolean) {
                    patchState(store, { isLoading: true });
                    waterTransferDocumentServiceStore.set({ isLoading: true });
                    waterNewContractServiceStore.set({ isLoading: true });
                    waterFinalInvoiceServiceStore.set({ isLoading: true });

                    try {
                        await firstValueFrom(waterTransferDocumentServiceService.setActive(transactionId, active));
                        const data = await firstValueFrom(waterCompositeService.get(transactionId));

                        const waterFinalInvoiceService = new WaterFinalInvoiceService(data.waterFinalInvoiceService);
                        const waterNewContractService = new WaterNewContractService(data.waterNewContractService);
                        const waterTransferDocumentService = new WaterTransferDocumentService(data.waterTransferDocumentService);

                        patchState(store, {
                            isLoading: false,
                            waterFinalInvoiceService,
                            waterNewContractService,
                            waterTransferDocumentService,
                        });

                        waterTransferDocumentServiceStore.set({ isLoading: false, waterTransferDocumentService });
                        waterNewContractServiceStore.set({ isLoading: false, service: waterNewContractService });
                        waterFinalInvoiceServiceStore.set({ isLoading: false, service: waterFinalInvoiceService });
                    } catch (error) {
                        patchState(store, { isLoading: false });
                        waterTransferDocumentServiceStore.set({ isLoading: false });
                        waterNewContractServiceStore.set({ isLoading: false });
                        waterFinalInvoiceServiceStore.set({ isLoading: false });
                    }
                },
            };
        },
    ),
    withHooks(({ load }) => {
        const transactionStore = inject(TransactionStore);

        effect(
            () => {
                if (transactionStore.transactionId()) {
                    load(transactionStore.transactionId());
                }
            },
            { allowSignalWrites: true },
        );

        return {
            onInit() {},
        };
    }),
);
